import {
  createStorage,
  uid as getUid,
  isEVMChain,
  isSolanaChain,
  noopStorage,
  registerChains,
  type Connection,
  type Connector,
  type ConnectorEventMap,
  type CreateWalletConnectorFn,
  type EventData,
  type PartializedState,
  type State,
  type Storage,
  type WalletConnector,
  type WalletConnectorType,
  type WalletConnectorsOption,
} from '@particle-network/connector-core';
import type { CreatePluginFn, Plugin } from '@particle-network/plugin-core';
import type { Chain } from 'viem/chains';
import { persist, subscribeWithSelector } from 'zustand/middleware';
import { createStore, type StoreApi } from 'zustand/vanilla';
import type { Appearance, ConnectKitOptions } from './context/types';
import { version } from './version';

export const createConfig = (options: ConnectKitOptions): Config => {
  const { walletConnectors: createWalletConnectorFns, chains, projectId, clientKey, appId, plugins } = options;
  if (!createWalletConnectorFns.length) {
    throw new Error('Please set at least one connector.');
  }

  if (!chains.length) {
    throw new Error('Please set at least one chain.');
  }

  registerChains(chains);

  if (
    options.initialChainId?.evm &&
    chains.some(isEVMChain) &&
    !chains.find((chain) => chain.id === options.initialChainId?.evm)
  ) {
    throw new Error('`chains` not includes `initialChainId.evm`');
  }

  if (
    options.initialChainId?.solana &&
    chains.some(isSolanaChain) &&
    !chains.find((chain) => chain.id === options.initialChainId?.solana)
  ) {
    throw new Error('`chains` not includes `initialChainId.solana`');
  }

  const storage = createStorage({
    storage: typeof window !== 'undefined' && window.localStorage ? window.localStorage : noopStorage,
  });

  function getInitialState(): State {
    return {
      chainId: chains[0].id,
      connections: new Map<string, Connection>(),
      current: null,
      status: 'disconnected',
    };
  }

  const currentVersion = Number(version.replaceAll('.', ''));
  const store = createStore(
    subscribeWithSelector(
      // only use persist middleware if storage exists
      storage
        ? persist(getInitialState, {
            migrate(persistedState, version) {
              // Now there is only one version.
              return persistedState as State;
            },
            name: 'store',
            partialize(state) {
              // Only persist "critical" store properties to preserve storage size.
              return {
                connections: {
                  __type: 'Map',
                  value: Array.from(state.connections.entries()).map(([key, connection]) => {
                    const { id, name, type, uid, chainType } = connection.connector;
                    const connector = { id, name, type, uid, chainType };
                    return [key, { ...connection, connector }];
                  }),
                } as unknown as PartializedState['connections'],
                chainId: state.chainId,
                current: state.current,
              } satisfies PartializedState;
            },
            storage: storage as Storage<Record<string, unknown>>,
            version: currentVersion,
          })
        : getInitialState
    )
  );

  /////////////////////////////////////////////////////////////////////////////////////////////////
  // Emitter listeners
  /////////////////////////////////////////////////////////////////////////////////////////////////

  function change(data: EventData<ConnectorEventMap, 'change'>) {
    store.setState((x) => {
      const connection = x.connections.get(data.uid);
      if (!connection) return x;
      return {
        ...x,
        connections: new Map(x.connections).set(data.uid, {
          accounts: (data.accounts as readonly [string, ...string[]]) ?? connection.accounts,
          chainId: data.chainId ?? connection.chainId,
          connector: connection.connector,
        }),
      };
    });
  }
  async function connect(data: EventData<ConnectorEventMap, 'connect'>) {
    const status = store.getState().status;
    // Disable handling if reconnecting/connecting
    if (status === 'connecting' || status === 'reconnecting') {
      console.log('Disable handling connect event because the status: ', status);
      return;
    }

    const connector = connectors.getState().find((x) => x.uid === data.uid);
    if (!connector) return;
    const isAuthorized = await connector.isAuthorized();
    if (!isAuthorized) {
      return;
    }

    console.log(`wallet auto connect, current status: ${status}, connectorId: ${connector.id}`);

    store.setState((x) => {
      const connector = connectors.getState().find((x) => x.uid === data.uid);
      if (!connector) return x;

      // unsupport auto connect
      // if (connector.emitter.listenerCount('connect')) connector.emitter.off('connect', connect);
      if (!connector.emitter.listenerCount('change')) connector.emitter.on('change', change);
      if (!connector.emitter.listenerCount('disconnect')) connector.emitter.on('disconnect', disconnect);

      storage.setItem('recentConnectorId', connector.id);

      console.log(`connector [${connector.id}] onConnect`);
      return {
        ...x,
        connections: new Map(x.connections).set(data.uid, {
          accounts: data.accounts as readonly [string, ...string[]],
          chainId: data.chainId,
          connector: connector,
        }),
        current: data.uid,
        status: 'connected',
      };
    });
  }
  function disconnect(data: EventData<ConnectorEventMap, 'disconnect'>) {
    store.setState((x) => {
      const connection = x.connections.get(data.uid);
      if (connection) {
        const connector = connection.connector;
        if (connector.emitter.listenerCount('change')) connection.connector.emitter.off('change', change);
        if (connector.emitter.listenerCount('disconnect')) connection.connector.emitter.off('disconnect', disconnect);
        // unsupport auto connect
        // if (!connector.emitter.listenerCount('connect')) connection.connector.emitter.on('connect', connect);
      }

      x.connections.delete(data.uid);

      if (x.connections.size === 0)
        return {
          ...x,
          connections: new Map(),
          current: null,
          status: 'disconnected',
        };

      const nextConnection = x.connections.values().next().value as Connection;
      return {
        ...x,
        connections: new Map(x.connections),
        current: nextConnection.connector.uid,
      };
    });
  }

  const connectors = createStore<Connector[]>(() => []);

  const connectorSetup = (connector: Connector) => {
    if (!connector.emitter.listenerCount('connect')) {
      // unsupport auto connect
      // connector.emitter.on('connect', connect);
    }
    return connector;
  };

  const setup = (createFn: CreateWalletConnectorFn) => {
    const walletConnector = createFn({
      chains,
      storage,
      projectId,
      clientKey,
      appId,
    } as WalletConnectorsOption);

    const subConnectors = walletConnector.connectors.map(connectorSetup);

    connectors.setState((x) => [...x, ...subConnectors], true);

    walletConnector.store.subscribe((state: Connector[]) => {
      const currentConnectorIds = new Map();
      for (const connector of connectors.getState()) {
        currentConnectorIds.set(connector.id, true);
      }

      const newConnectors: Connector[] = [];
      for (const connector of state) {
        if (currentConnectorIds.has(connector.id)) continue;
        newConnectors.push(connector);
      }
      if (newConnectors.length) {
        connectors.setState((x) => [...x, ...newConnectors], true);
      }
    });

    return walletConnector;
  };

  const walletConnectors = createWalletConnectorFns.map(setup);

  const getWalletConnector = <properties extends Record<string, unknown> = Record<string, unknown>>(
    type: WalletConnectorType
  ) => {
    const walletConnector = walletConnectors.find((item) => item.type === type);
    if (walletConnector) {
      return walletConnector as WalletConnector<properties>;
    }
    return undefined;
  };

  // Update default chain when connector chain changes
  store.subscribe(
    ({ connections, current }) => (current ? connections.get(current)?.chainId : undefined),
    (chainId) => {
      // If chain is not configured, then don't switch over to it.
      const isChainConfigured = chains.some((x) => x.id === chainId);
      if (!isChainConfigured) return;

      return store.setState((x) => ({
        ...x,
        chainId: chainId ?? x.chainId,
      }));
    }
  );

  function setupPlugin(createPluginFn: CreatePluginFn): Plugin {
    return createPluginFn({
      projectConfig: {
        projectId,
        clientKey,
        appId,
      },
      chains,
    });
  }

  const _plugins = plugins?.map(setupPlugin);

  const uid = getUid();

  const _appearance = options.appearance || {};

  const appearanceStore = createStore(() => _appearance);

  return {
    get uid() {
      return uid;
    },
    get plugins() {
      return _plugins || [];
    },
    get options() {
      return options;
    },
    get chains() {
      return chains;
    },
    get connectors() {
      return connectors.getState();
    },
    get appearanceStore() {
      return appearanceStore;
    },
    storage,
    get walletConnectors() {
      return walletConnectors;
    },
    getWalletConnector,
    get state() {
      return store.getState() as unknown as State;
    },
    setState(value) {
      let newState: State;
      if (typeof value === 'function') newState = value(store.getState() as any);
      else newState = value;

      // Reset state if it got set to something not matching the base state
      const initialState = getInitialState();
      if (typeof newState !== 'object') newState = initialState;
      const isCorrupt = Object.keys(initialState).some((x) => !(x in newState));
      if (isCorrupt) newState = initialState;

      store.setState(newState, true);
    },
    subscribe(selector, listener, options) {
      return store.subscribe(
        selector as unknown as (state: State) => any,
        listener,
        options ? { ...options, fireImmediately: options.emitImmediately } : undefined
      );
    },
    _internal: {
      connectors: {
        setup: connectorSetup,
        subscribe(listener) {
          return connectors.subscribe(listener);
        },
      },
      events: { change, connect, disconnect },
    },
  };
};

export type Config = {
  readonly uid: string;
  readonly options: ConnectKitOptions;
  readonly plugins: Plugin[];
  readonly chains: readonly [Chain, ...Chain[]];
  readonly connectors: readonly Connector[];
  readonly storage: Storage | null;
  readonly walletConnectors: readonly WalletConnector[];
  readonly state: State;
  readonly appearanceStore: StoreApi<Appearance>;
  setState(value: State | ((state: State) => State)): void;
  subscribe<state>(
    selector: (state: State) => state,
    listener: (state: state, previousState: state) => void,
    options?:
      | {
          emitImmediately?: boolean | undefined;
          equalityFn?: ((a: state, b: state) => boolean) | undefined;
        }
      | undefined
  ): () => void;
  getWalletConnector: <properties extends Record<string, unknown> = Record<string, unknown>>(
    type: WalletConnectorType
  ) => WalletConnector<properties> | undefined;
  _internal: {
    connectors: {
      setup(connector: Connector): Connector;
      subscribe(listener: (state: Connector[], prevState: Connector[]) => void): () => void;
    };
    events: {
      change(data: EventData<ConnectorEventMap, 'change'>): void;
      connect(data: EventData<ConnectorEventMap, 'connect'>): void;
      disconnect(data: EventData<ConnectorEventMap, 'disconnect'>): void;
    };
  };
};
